import { useEffect, useRef, useState } from "react";
import { AppId } from "../../../app/appTypes";
import { useAppDispatch } from "../../../app/hooks";
import { Alert, Box, Button, Card, CardActionArea, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import { loadAllUrsOffers, Offers } from "../UrsSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { Share, Link as LinkIcon } from "@mui/icons-material";
import { CompanyBrowserForm, CompanyBrowserFormRef } from "../CompanyBrowserForm";
import { PublishLink } from "../PublishLink";

type PublishMethod = "vchainer" | "link";

interface WizardPublishDialogProps {
    open: boolean;
    ursId: AppId;
    processing: boolean;
    cancel: () => void;
    confirm: (
        method: PublishMethod,
        selectedCompanies: Set<AppId>,
        supplierIds: Set<AppId>
    ) => void;
}


export const WizardPublishDialog = ({
    open,
    ursId,
    processing,
    cancel,
    confirm,
}: WizardPublishDialogProps) => {
    const dispatch = useAppDispatch();

    const [step, setStep] = useState(1);
    const [publishMethod, setPublishMethod] = useState<PublishMethod | null>(null);
    const [offers, setOffers] = useState<Offers[] | null>(null)
    const browserRef = useRef<CompanyBrowserFormRef>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            dispatch(loadAllUrsOffers(ursId))
                .then(unwrapResult)
                .then(setOffers)
                .catch((err) => console.error(err));
        }
    }, [open]);

    if (!offers) return <Skeleton />;

    const supplierFilter = new Set<AppId>(offers.map(x => x.supplierId))

    const handleNextFromStep1 = (method: PublishMethod) => {
        setPublishMethod(method);
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
        setPublishMethod(null);
    };

    const handleConfirm = () => {
        if (publishMethod) {
            const companies = browserRef.current?.getSelectedCompanies() ?? new Set();
            const suppliers = browserRef.current?.getSelectedSuppliers() ?? new Set();

            if (publishMethod === "vchainer" && companies.size === 0 && suppliers.size === 0) {
                setValidationError("Wybierz przynajmniej jednego dostawcę lub firmę, aby kontynuować.");
                return;
            }

            setValidationError(null); // brak błędu
            confirm(publishMethod, companies, suppliers);
        }
    };


    return (
        <Dialog open={open} onClose={cancel} fullWidth maxWidth="md">
            <DialogTitle>Publish URS Document</DialogTitle>

            <DialogContent dividers>
                {step === 1 && (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        "&:hover": {
                                            borderColor: "success.main",
                                            borderWidth: 2,
                                        },
                                    }}
                                >
                                    <CardActionArea onClick={() => handleNextFromStep1("vchainer")}>
                                        <CardContent>
                                            <Share fontSize="large" sx={{ mb: 1 }} />
                                            <Typography variant="h6">
                                                Publish in V-Chainer
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Send document in system V-Chainer.
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        "&:hover": {
                                            borderColor: "success.main",
                                            borderWidth: 2,
                                        },
                                    }}
                                >
                                    <CardActionArea onClick={() => handleNextFromStep1("link")}>
                                        <CardContent>
                                            <LinkIcon fontSize="large" sx={{ mb: 1 }} />
                                            <Typography variant="h6">
                                                Publish through public link
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Generates access link to the document.
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        </Grid>

                    </>
                )}
                {step === 2 && publishMethod && (
                    <>
                        <Paper
                            variant="outlined"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                p: 2,
                                mb: 2,
                                backgroundColor: "grey.100",
                                borderColor: "success.main",
                                borderWidth: 2,
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                {publishMethod === "vchainer" ? (
                                    <Share color="primary" />
                                ) : (
                                    <LinkIcon color="primary" />
                                )}
                                <Box>
                                    <Typography variant="body1" fontWeight="bold">
                                        {publishMethod === "vchainer"
                                            ? "Publish in V-Chainer"
                                            : "Publish through public link"}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {publishMethod === "vchainer"
                                            ? "System will send notification emails to selected suppliers."
                                            : " Copy the link and share it with people who should have access to the document. The system does not send the link automatically. Optionally, you can choose who you want to publish the link to."}
                                    </Typography>
                                </Box>
                            </Box>

                            {publishMethod === "link" && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 2,
                                        backgroundColor: "grey.50",
                                        border: "1px solid",
                                        borderColor: "success.main",
                                        borderRadius: 1,
                                    }}
                                >
                                    <PublishLink objectId={ursId} objectType="URS_REQUEST" />
                                </Box>
                            )}
                        </Paper>



                        <CompanyBrowserForm
                            ref={browserRef}
                            filter={supplierFilter}
                            processing={processing}
                        />
                        {validationError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {validationError}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={cancel} disabled={processing}>Cancel</Button>
                {step === 2 && (
                    <>
                        <Button onClick={handleBack} disabled={processing}>
                            Back
                        </Button>
                        <Button onClick={handleConfirm} disabled={processing || !publishMethod} variant="contained">
                            Publish
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
